// AI Analysis Custom Hook
// Extracted from TradingJournal.js for better maintainability
// Contains: analyzePatterns, generateSuggestions, parseNaturalLanguageTrade, generateDailyReview

import { useCallback, useMemo } from 'react';
import { getTradeNetPnL } from '../config';
import { safePnl } from '../utils/formatters';
import { calcTradeStats, calcRStats, calcDailyStats, calcStreaks, calcGroupedStats } from '../services/statsCalculator';
import { coach, toLegacyReviewShape } from '../services/coach';
import { tradeDateInLastNDays, getCurrentTradingDay, weekStartOfDateStr, addDaysToDateStr, tradeDateInRange } from '../services/timeService';

/**
 * Custom hook for AI-powered trade analysis
 * @param {Object} params - Hook parameters
 * @param {Array} params.trades - All trades
 * @param {Array} params.accounts - All accounts
 * @param {Array} params.setups - Available setups
 * @param {Array} params.eventTags - Available event tags
 * @param {Object} params.journalEntries - Journal entries by date
 * @param {boolean} params.activeAccountsOnly - Filter for active accounts only
 * @returns {Object} Analysis functions
 */
const useAIAnalysis = ({
  trades,
  accounts,
  setups,
  eventTags,
  journalEntries,
  activeAccountsOnly,
  includePassedInStats = false,
  includeResetTradesInStats = true,
  timePeriodFilter = 'all',
  selectedAccountFilter = 'all'
}) => {

  // Helper: Get active account IDs — mirrors useTradeFiltering exactly
  // Includes Passed accounts when includePassedInStats is ON
  const activeAccountIds = useMemo(() => {
    return activeAccountsOnly
      ? accounts.filter(a => a.status === 'Active' || (includePassedInStats && a.status === 'Passed')).map(a => a.id)
      : accounts.map(a => a.id);
  }, [accounts, activeAccountsOnly, includePassedInStats]);

  // Build accountsById lookup once for reset-period filtering
  const accountsById = useMemo(() => {
    const map = {};
    accounts.forEach(a => { map[a.id] = a; });
    return map;
  }, [accounts]);

  // Helper: Filter trades — mirrors useTradeFiltering pipeline exactly
  // This ensures AI Insights shows the same data as Dashboard/Overview
  const getFilteredTrades = useCallback((tradesToFilter) => {
    let result = tradesToFilter;

    // Filter by selected account if not "all"
    if (selectedAccountFilter !== 'all') {
      result = result.filter(t => t.accountId === selectedAccountFilter);
    }

    // Filter by active accounts only if enabled (and "all" is selected)
    if (activeAccountsOnly && selectedAccountFilter === 'all') {
      result = result.filter(t => !t.accountId || activeAccountIds.includes(t.accountId));
    }

    // Filter out pre-reset trades if the toggle is off
    if (!includeResetTradesInStats) {
      result = result.filter(t => {
        if (!t.accountId) return true;
        const account = accountsById[t.accountId];
        if (!account) return true;
        const currentPeriod = account.currentPeriod || 1;
        return !t.period || t.period === currentPeriod;
      });
    }

    // Filter by time period — string compare anchored at the current trading day.
    if (timePeriodFilter !== 'all') {
      const days = parseInt(timePeriodFilter);
      if (Number.isFinite(days) && days > 0) {
        result = result.filter(t => tradeDateInLastNDays(t.date, days));
      }
    }

    // Exclude open/in-progress trades (matches closedFilteredTrades)
    result = result.filter(t => !t.isOpen);

    return result;
  }, [activeAccountsOnly, activeAccountIds, accountsById, includeResetTradesInStats, timePeriodFilter, selectedAccountFilter]);


  // ============================================
  // GENERATE SUGGESTIONS
  // ============================================
  const generateSuggestions = useCallback((
    setupPerformance, 
    topMistakes, 
    directionAnalysis, 
    sessions, 
    recentPerf, 
    olderPerf, 
    streaks, 
    avgR, 
    revengeStats, 
    weekComp, 
    optimalSize
  ) => {
    const suggestions = [];
    
    // ===== POSITIVE INSIGHTS (SUCCESS) =====
    
    // Top performing setups (70%+ WR with 10+ trades)
    const topSetups = Object.entries(setupPerformance)
      .filter(([_, data]) => parseFloat(data.winRate) >= 60 && data.count >= 10)
      .sort((a, b) => parseFloat(b[1].winRate) - parseFloat(a[1].winRate))
      .slice(0, 3);
    
    topSetups.forEach(([setup, data]) => {
      const pnl = data.totalPnL || 0;
      suggestions.push({
        type: 'success',
        category: 'Edge',
        title: `${setup}`,
        message: `${data.winRate}% win rate over ${data.count} trades`,
        details: `Total P&L: ${pnl >= 0 ? '+' : '-'}$${Math.abs(pnl).toFixed(2)} | Avg: $${(pnl / data.count).toFixed(2)}/trade`,
        action: 'Focus on this setup - it\'s your edge'
      });
    });
    
    // Excellent R-Multiple
    if (avgR >= 1.5) {
      suggestions.push({
        type: 'success',
        category: 'Risk Management',
        title: `Strong R-Multiple: ${avgR.toFixed(2)}R`,
        message: `You're letting winners run properly`,
        details: `Target: 1.5R+ | Your Avg: ${avgR.toFixed(2)}R`,
        action: 'Keep this discipline - it compounds over time'
      });
    }
    
    // Improving performance trend
    if (recentPerf && olderPerf) {
      const recentWR = parseFloat(recentPerf.winRate);
      const olderWR = parseFloat(olderPerf.winRate);
      if (recentWR > olderWR + 10) {
        suggestions.push({
          type: 'success',
          category: 'Momentum',
          title: `Performance Improving`,
          message: `Recent: ${recentWR}% WR vs Previous: ${olderWR}% WR`,
          details: `Last 20 trades: ${recentWR >= 0 ? '+' : ''}$${recentPerf.pnl?.toFixed(2) || '0.00'}`,
          action: 'You\'re in the zone - stay focused'
        });
      }
    }
    
    // Great week
    if (weekComp && weekComp.thisWeek.trades >= 3 && weekComp.lastWeek.trades >= 3) {
      if (weekComp.thisWeek.pnl > weekComp.lastWeek.pnl * 1.5 && weekComp.thisWeek.pnl > 0) {
        suggestions.push({
          type: 'success',
          category: 'Weekly',
          title: `Strong Week`,
          message: `+$${weekComp.thisWeek.pnl.toFixed(2)} this week`,
          details: `vs $${weekComp.lastWeek.pnl.toFixed(2)} last week | ${weekComp.thisWeek.trades} trades`,
          action: 'Document what\'s working this week'
        });
      }
    }
    
    // Best session
    if (sessions) {
      const sessionArr = Object.entries(sessions).filter(([_, d]) => d.count >= 5);
      if (sessionArr.length >= 2) {
        const best = sessionArr.reduce((b, s) => parseFloat(s[1].winRate) > parseFloat(b[1].winRate) ? s : b);
        if (parseFloat(best[1].winRate) >= 50 && best[1].totalPnL > 0) {
          suggestions.push({
            type: 'success',
            category: 'Time',
            title: `Best Session: ${best[1].name}`,
            message: `${best[1].winRate}% win rate with ${best[1].count} trades`,
            details: `Total: +$${best[1].totalPnL.toFixed(2)}`,
            action: 'Prioritize trading during this session'
          });
        }
      }
    }
    
    // ===== ACTIONABLE INSIGHTS (INFO) =====
    
    // Direction bias
    if (directionAnalysis) {
      const longWR = parseFloat(directionAnalysis.long.winRate);
      const shortWR = parseFloat(directionAnalysis.short.winRate);
      const longPnL = directionAnalysis.long.totalPnL || 0;
      const shortPnL = directionAnalysis.short.totalPnL || 0;
      
      if (directionAnalysis.long.count >= 10 && directionAnalysis.short.count >= 10) {
        if (longWR > shortWR + 15) {
          suggestions.push({
            type: 'info',
            category: 'Direction Bias',
            title: `Long Trades Outperform`,
            message: `Long: ${longWR}% WR vs Short: ${shortWR}% WR`,
            details: `Long P&L: ${longPnL >= 0 ? '+' : ''}$${longPnL.toFixed(2)} | Short P&L: ${shortPnL >= 0 ? '+' : ''}$${shortPnL.toFixed(2)}`,
            action: 'Be more selective with short entries'
          });
        } else if (shortWR > longWR + 15) {
          suggestions.push({
            type: 'info',
            category: 'Direction Bias',
            title: `Short Trades Outperform`,
            message: `Short: ${shortWR}% WR vs Long: ${longWR}% WR`,
            details: `Short P&L: ${shortPnL >= 0 ? '+' : ''}$${shortPnL.toFixed(2)} | Long P&L: ${longPnL >= 0 ? '+' : ''}$${longPnL.toFixed(2)}`,
            action: 'Be more selective with long entries'
          });
        }
      }
    }
    
    // Position sizing insight
    if (optimalSize && optimalSize.count >= 5) {
      suggestions.push({
        type: 'info',
        category: 'Position Size',
        title: `Optimal Size: ${optimalSize.bucket} Contract${optimalSize.bucket !== '1' ? 's' : ''}`,
        message: `${optimalSize.winRate}% win rate at this size`,
        details: `Avg P&L: $${optimalSize.avgPnL}/trade over ${optimalSize.count} trades`,
        action: 'Consider standardizing position size'
      });
    }
    
    // ===== WARNINGS (AREAS TO IMPROVE) =====
    
    // Underperforming setups (< 40% WR with 10+ trades) - limit to top 3 worst
    const weakSetups = Object.entries(setupPerformance)
      .filter(([_, data]) => parseFloat(data.winRate) < 40 && data.count >= 10)
      .sort((a, b) => parseFloat(a[1].winRate) - parseFloat(b[1].winRate))
      .slice(0, 3);
    
    weakSetups.forEach(([setup, data]) => {
      const pnl = data.totalPnL || 0;
      suggestions.push({
        type: 'warning',
        category: 'Setup Review',
        title: `${setup}`,
        message: `Only ${data.winRate}% win rate over ${data.count} trades`,
        details: `Total P&L: ${pnl >= 0 ? '+' : '-'}$${Math.abs(pnl).toFixed(2)} | Avg: $${(pnl / data.count).toFixed(2)}/trade`,
        action: 'Review entry criteria or consider removing from playbook'
      });
    });
    
    // Declining performance
    if (recentPerf && olderPerf) {
      const recentWR = parseFloat(recentPerf.winRate);
      const olderWR = parseFloat(olderPerf.winRate);
      if (olderWR > recentWR + 10) {
        suggestions.push({
          type: 'warning',
          category: 'Trend Alert',
          title: `Performance Declining`,
          message: `Recent: ${recentWR}% WR vs Previous: ${olderWR}% WR`,
          details: `Last 20 trades: ${recentPerf.pnl >= 0 ? '+' : ''}$${recentPerf.pnl?.toFixed(2) || '0.00'}`,
          action: 'Review recent trades - what changed?'
        });
      }
    }
    
    // Most common mistake
    if (topMistakes.length > 0 && topMistakes[0][1] >= 5) {
      suggestions.push({
        type: 'warning',
        category: 'Discipline',
        title: `Top Mistake: ${topMistakes[0][0]}`,
        message: `Occurred ${topMistakes[0][1]} times`,
        details: topMistakes.length > 1 ? `Also watch: ${topMistakes.slice(1, 3).map(m => m[0]).join(', ')}` : null,
        action: 'Add to pre-trade checklist'
      });
    }
    
    // Poor R-Multiple
    if (avgR < 0.5 && avgR > 0) {
      suggestions.push({
        type: 'warning',
        category: 'Risk Management',
        title: `Low R-Multiple: ${avgR.toFixed(2)}R`,
        message: `You may be cutting winners too early`,
        details: `Target: 1.5R+ | Current Avg: ${avgR.toFixed(2)}R`,
        action: 'Consider wider targets or trailing stops'
      });
    }
    
    // Losing streak
    if (streaks && streaks.current <= -3) {
      suggestions.push({
        type: 'warning',
        category: 'Psychology',
        title: `${Math.abs(streaks.current)}-Trade Losing Streak`,
        message: `Time to step back and reset`,
        details: `Best approach: Take a break, review process, return fresh`,
        action: 'Reduce size or pause trading today'
      });
    }
    
    // Revenge trading
    if (revengeStats && revengeStats.instances >= 2) {
      suggestions.push({
        type: 'warning',
        category: 'Psychology',
        title: `Revenge Trading Detected`,
        message: `${revengeStats.instances} instances identified`,
        details: `Cost: -$${Math.abs(revengeStats.totalPnL).toFixed(2)}`,
        action: 'Implement a mandatory break after losses'
      });
    }
    
    // Worst session warning
    if (sessions) {
      const sessionArr = Object.entries(sessions).filter(([_, d]) => d.count >= 5);
      if (sessionArr.length >= 2) {
        const worst = sessionArr.reduce((w, s) => s[1].totalPnL < w[1].totalPnL ? s : w);
        if (worst[1].totalPnL < -100) {
          suggestions.push({
            type: 'warning',
            category: 'Time',
            title: `Avoid: ${worst[1].name}`,
            message: `${worst[1].winRate}% win rate with ${worst[1].count} trades`,
            details: `Total: -$${Math.abs(worst[1].totalPnL).toFixed(2)}`,
            action: 'Consider skipping this session'
          });
        }
      }
    }
    
    // Down week warning
    if (weekComp && weekComp.thisWeek.trades >= 3 && weekComp.lastWeek.trades >= 3) {
      if (weekComp.thisWeek.pnl < 0 && weekComp.lastWeek.pnl > 0) {
        suggestions.push({
          type: 'warning',
          category: 'Weekly',
          title: `Down Week`,
          message: `-$${Math.abs(weekComp.thisWeek.pnl).toFixed(2)} this week`,
          details: `vs +$${weekComp.lastWeek.pnl.toFixed(2)} last week`,
          action: 'Review what changed from last week'
        });
      }
    }
    
    return suggestions;
  }, []);



  // ============================================
  // PARSE NATURAL LANGUAGE TRADE
  // ============================================
  const parseNaturalLanguageTrade = useCallback((input) => {
    const trade = {
      // Trade dates anchor to the current trading day (5:15pm ET roll), so
      // an evening parse goes onto the upcoming session — not yesterday's.
      date: getCurrentTradingDay(),
      symbol: '',
      entry: '',
      exits: [],
      quantity: '1',
      setup: '',
      stopLoss: '',
      stopLossPoints: '',
      useStopLossPoints: false,
      tags: [],
      mistakes: [],
      notes: ''
    };
    
    const symbolMatch = input.match(/\b(NQ|ES|MNQ|MES|YM|MYM|RTY|M2K|GC|MGC|SI|SIL)\b/i);
    if (symbolMatch) trade.symbol = symbolMatch[1].toUpperCase();
    
    // Match quantity - look for patterns like "5 MNQ", "5 contracts", etc.
    const qtyMatch = input.match(/(\d+)\s*(?:contracts?|lots?|NQ|ES|MNQ|MES)/i);
    if (qtyMatch) trade.quantity = qtyMatch[1];
    
    // Extract prices
    const prices = input.match(/\d+(?:,\d{3})*(?:\.\d+)?/g);
    if (prices && prices.length >= 2) {
      const cleanPrices = prices.map(p => p.replace(/,/g, ''));
      trade.entry = cleanPrices[0];
      
      // Check for stop loss mentioned with points
      const stopPointsMatch = input.match(/(\d+)\s*(?:point|pt|tick)s?\s*stop/i);
      if (stopPointsMatch) {
        trade.stopLossPoints = stopPointsMatch[1];
        trade.useStopLossPoints = true;
      } else if (prices.length >= 3 && (input.toLowerCase().includes('stop') || input.toLowerCase().includes('sl'))) {
        // Stop loss as a price level
        trade.stopLoss = cleanPrices[cleanPrices.length - 1];
      }
      
      // Parse exits - all remaining prices except entry and stop become exits
      const totalQty = parseInt(trade.quantity) || 1;
      const exitPrices = cleanPrices.slice(1);
      
      // Remove stop loss price if it was the last one
      if (trade.stopLoss && exitPrices.length > 0 && exitPrices[exitPrices.length - 1] === trade.stopLoss) {
        exitPrices.pop();
      }
      
      if (exitPrices.length === 1) {
        trade.exits = [{ quantity: totalQty.toString(), price: exitPrices[0] }];
      } else if (exitPrices.length > 1) {
        trade.exits = [{ quantity: totalQty.toString(), price: exitPrices[0] }];
      } else {
        trade.exits = [{ quantity: '', price: '' }];
      }
    } else {
      trade.exits = [{ quantity: '', price: '' }];
    }
    
    // Match setups
    setups.forEach(setup => {
      if (input.toLowerCase().includes(setup.toLowerCase())) {
        trade.setup = setup;
      }
    });
    
    // Match event tags
    eventTags.forEach(tag => {
      if (input.toLowerCase().includes(tag.toLowerCase())) {
        trade.tags.push(tag);
      }
    });
    
    return trade;
  }, [setups, eventTags]);



  // ============================================
  // ANALYZE PATTERNS (Main AI Analysis)
  // ============================================
  const analyzePatterns = useCallback(() => {
    if (trades.length < 5) return null;
    
    const filteredTrades = getFilteredTrades(trades);
    if (filteredTrades.length < 5) return null;
    
    // === SETUP PERFORMANCE ===
    const setupGrouped = calcGroupedStats(filteredTrades, t => t.setup || 'Unknown');
    const setupPerformance = {};
    Object.entries(setupGrouped).forEach(([setup, s]) => {
      const rMultiples = s.trades.filter(t => t.rMultiple).map(t => parseFloat(t.rMultiple));
      setupPerformance[setup] = {
        count: s.totalTrades,
        wins: s.wins,
        totalPnL: s.totalPnL,
        winRate: s.winRate.toFixed(1),
        avgPnL: s.avgPnL.toFixed(2),
        avgRMultiple: rMultiples.length > 0
          ? (rMultiples.reduce((a, b) => a + b, 0) / rMultiples.length).toFixed(2) : 0,
        rMultiples
      };
    });

    const bestSetup = Object.entries(setupPerformance)
      .filter(([_, d]) => d.count >= 3)
      .sort((a, b) => parseFloat(b[1].winRate) - parseFloat(a[1].winRate))[0];

    const mostProfitableSetup = Object.entries(setupPerformance)
      .filter(([_, d]) => d.count >= 3)
      .sort((a, b) => b[1].totalPnL - a[1].totalPnL)[0];


    // === MISTAKE ANALYSIS ===
    const mistakeFrequency = {};
    let tradesWithMistakes = 0;
    let mistakePnL = 0;
    filteredTrades.forEach(trade => {
      if (trade.mistakes && trade.mistakes.length > 0) {
        tradesWithMistakes++;
        mistakePnL += safePnl(trade);
        trade.mistakes.forEach(mistake => {
          mistakeFrequency[mistake] = (mistakeFrequency[mistake] || 0) + 1;
        });
      }
    });
    const topMistakes = Object.entries(mistakeFrequency).sort((a, b) => b[1] - a[1]).slice(0, 5);
    
    // === OVERALL METRICS ===
    const overallStats = calcTradeStats(filteredTrades);
    const { totalPnL, winRate, profitFactor, avgWin, avgLoss } = overallStats;
    const overallR = calcRStats(filteredTrades);
    const avgR = overallR.avgR;
    const totalR = overallR.totalR;
    const _tradesWithR = filteredTrades.filter(t => t.rMultiple && parseFloat(t.rMultiple) !== 0);


    // === SYMBOL PERFORMANCE ===
    const symbolGrouped = calcGroupedStats(filteredTrades, t => t.symbol || 'Unknown');
    const symbolPerformance = {};
    Object.entries(symbolGrouped).forEach(([symbol, s]) => {
      const rMultiples = s.trades.filter(t => t.rMultiple).map(t => parseFloat(t.rMultiple));
      symbolPerformance[symbol] = {
        count: s.totalTrades,
        wins: s.wins,
        totalPnL: s.totalPnL,
        winRate: s.winRate.toFixed(1),
        avgPnL: s.avgPnL.toFixed(2),
        avgR: rMultiples.length > 0 ? (rMultiples.reduce((a, b) => a + b, 0) / rMultiples.length).toFixed(2) : '0',
        rMultiples
      };
    });
    const sortedSymbols = Object.entries(symbolPerformance).sort((a, b) => b[1].totalPnL - a[1].totalPnL);
    
    // === DIRECTION ANALYSIS ===
    const longTrades = filteredTrades.filter(t => t.direction === 'long');
    const shortTrades = filteredTrades.filter(t => t.direction === 'short');
    const longStats = calcTradeStats(longTrades);
    const shortStats = calcTradeStats(shortTrades);
    const directionAnalysis = {
      long: {
        count: longTrades.length,
        wins: longStats.wins,
        winRate: longStats.winRate.toFixed(1),
        totalPnL: longStats.totalPnL,
        avgPnL: longStats.avgPnL.toFixed(2)
      },
      short: {
        count: shortTrades.length,
        wins: shortStats.wins,
        winRate: shortStats.winRate.toFixed(1),
        totalPnL: shortStats.totalPnL,
        avgPnL: shortStats.avgPnL.toFixed(2)
      }
    };


    // === ATM STRATEGY ANALYSIS ===
    const tradesWithAtm = filteredTrades.filter(t => t.atmStrategy);
    const atmGrouped = calcGroupedStats(tradesWithAtm, t => t.atmStrategy);
    const atmPerformance = {};
    Object.entries(atmGrouped).forEach(([atm, s]) => {
      const rMultiples = s.trades.filter(t => t.rMultiple).map(t => parseFloat(t.rMultiple));
      atmPerformance[atm] = {
        count: s.totalTrades,
        wins: s.wins,
        totalPnL: s.totalPnL,
        winRate: s.winRate.toFixed(1),
        avgPnL: s.avgPnL.toFixed(2),
        avgR: rMultiples.length > 0 ? (rMultiples.reduce((a, b) => a + b, 0) / rMultiples.length).toFixed(2) : '0',
        rMultiples
      };
    });
    const sortedAtm = Object.entries(atmPerformance).sort((a, b) => b[1].totalPnL - a[1].totalPnL);
    
    // === TIME ANALYSIS ===
    const getHour = (trade) => {
      if (!trade.entryTime) return null;
      if (trade.entryTime.includes('/')) {
        const parts = trade.entryTime.split(' ');
        if (parts.length >= 3) {
          const [h] = parts[1].split(':');
          let hour = parseInt(h);
          if (parts[2]?.toUpperCase() === 'PM' && hour !== 12) hour += 12;
          if (parts[2]?.toUpperCase() === 'AM' && hour === 12) hour = 0;
          return hour;
        }
      } else {
        return parseInt(trade.entryTime.split(':')[0]);
      }
      return null;
    };
    
    const tradesWithTime = filteredTrades.filter(t => t.entryTime);
    const hourlyPerformance = {};
    tradesWithTime.forEach(trade => {
      const hour = getHour(trade);
      if (hour === null) return;
      if (!hourlyPerformance[hour]) {
        hourlyPerformance[hour] = { count: 0, wins: 0, totalPnL: 0 };
      }
      hourlyPerformance[hour].count++;
      if (safePnl(trade) > 0) hourlyPerformance[hour].wins++;
      hourlyPerformance[hour].totalPnL += safePnl(trade);
    });
    Object.keys(hourlyPerformance).forEach(hour => {
      const data = hourlyPerformance[hour];
      data.winRate = (data.wins / data.count * 100).toFixed(1);
    });


    const bestHour = Object.entries(hourlyPerformance)
      .filter(([_, d]) => d.count >= 3)
      .sort((a, b) => b[1].totalPnL - a[1].totalPnL)[0];
    const worstHour = Object.entries(hourlyPerformance)
      .filter(([_, d]) => d.count >= 3)
      .sort((a, b) => a[1].totalPnL - b[1].totalPnL)[0];
    
    // Day of week analysis
    const dayOfWeekPerformance = { 0: null, 1: null, 2: null, 3: null, 4: null, 5: null, 6: null };
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    filteredTrades.forEach(trade => {
      const day = new Date(trade.date + 'T12:00:00').getDay();
      if (!dayOfWeekPerformance[day]) {
        dayOfWeekPerformance[day] = { name: dayNames[day], count: 0, wins: 0, totalPnL: 0 };
      }
      dayOfWeekPerformance[day].count++;
      if (safePnl(trade) > 0) dayOfWeekPerformance[day].wins++;
      dayOfWeekPerformance[day].totalPnL += safePnl(trade);
    });
    Object.keys(dayOfWeekPerformance).forEach(day => {
      if (dayOfWeekPerformance[day]) {
        const data = dayOfWeekPerformance[day];
        data.winRate = (data.wins / data.count * 100).toFixed(1);
      }
    });
    const activeDays = Object.entries(dayOfWeekPerformance)
      .filter(([_, d]) => d && d.count > 0)
      .sort((a, b) => b[1].totalPnL - a[1].totalPnL);
    
    // Session analysis
    const sessionPerformance = {
      morning: { name: 'Morning (before 11am)', count: 0, wins: 0, totalPnL: 0 },
      midday: { name: 'Midday (11am-2pm)', count: 0, wins: 0, totalPnL: 0 },
      afternoon: { name: 'Afternoon (after 2pm)', count: 0, wins: 0, totalPnL: 0 }
    };
    tradesWithTime.forEach(trade => {
      const hour = getHour(trade);
      if (hour === null) return;
      let session = hour < 11 ? 'morning' : hour < 14 ? 'midday' : 'afternoon';
      sessionPerformance[session].count++;
      if (safePnl(trade) > 0) sessionPerformance[session].wins++;
      sessionPerformance[session].totalPnL += safePnl(trade);
    });
    Object.keys(sessionPerformance).forEach(session => {
      const data = sessionPerformance[session];
      data.winRate = data.count > 0 ? (data.wins / data.count * 100).toFixed(1) : '0';
    });


    // === STREAK ANALYSIS ===
    const { currentStreak, bestWinStreak: maxWinStreak, worstLossStreak: maxLossStreak } = calcStreaks(filteredTrades);
    const streaksData = { current: currentStreak, maxWin: maxWinStreak, maxLoss: maxLossStreak };
    
    // === DAILY PERFORMANCE ===
    const dailyCalc = calcDailyStats(filteredTrades);
    const dailyPnL = {};
    filteredTrades.forEach(trade => {
      if (!dailyPnL[trade.date]) {
        dailyPnL[trade.date] = { pnl: 0, trades: 0, wins: 0 };
      }
      dailyPnL[trade.date].pnl += safePnl(trade);
      dailyPnL[trade.date].trades++;
      if (safePnl(trade) > 0) dailyPnL[trade.date].wins++;
    });
    const tradingDays = Object.keys(dailyPnL);
    const greenDays = dailyCalc.greenDays;
    const redDays = dailyCalc.redDays;
    const avgDailyPnL = dailyCalc.avgDailyPnL;
    const avgTradesPerDay = dailyCalc.tradesPerDay;
    const sortedDays = Object.entries(dailyPnL).sort((a, b) => b[1].pnl - a[1].pnl);
    const bestDay = sortedDays[0];
    const worstDay = sortedDays[sortedDays.length - 1];


    // === RECENT PERFORMANCE (Last 20 trades vs previous) ===
    const sortedByDate = [...filteredTrades].sort((a, b) => {
      const dateA = new Date(a.date + 'T' + (a.entryTime || '12:00'));
      const dateB = new Date(b.date + 'T' + (b.entryTime || '12:00'));
      return dateA - dateB;
    });
    const recentTrades = sortedByDate.slice(-20);
    const olderTrades = sortedByDate.slice(0, -20);
    const recentStats = calcTradeStats(recentTrades);
    const recentPerformance = {
      count: recentStats.totalTrades,
      pnl: recentStats.totalPnL,
      winRate: recentStats.winRate.toFixed(1)
    };
    const olderStats = olderTrades.length >= 10 ? calcTradeStats(olderTrades) : null;
    const olderPerformance = olderStats ? {
      count: olderStats.totalTrades,
      pnl: olderStats.totalPnL,
      winRate: olderStats.winRate.toFixed(1)
    } : null;
    
    // === ACCOUNT PERFORMANCE ===
    const accountGrouped = calcGroupedStats(filteredTrades, t => t.accountId || 'unassigned');
    const accountPerformance = {};
    Object.entries(accountGrouped).forEach(([accId, s]) => {
      const account = accounts.find(a => a.id === accId);
      accountPerformance[accId] = {
        count: s.totalTrades,
        wins: s.wins,
        totalPnL: s.totalPnL,
        winRate: s.winRate.toFixed(1),
        name: account ? account.name : accId
      };
    });
    
    // === EQUITY CURVE DATA ===
    let runningPnL = 0;
    const equityCurve = sortedByDate.map((trade, i) => {
      runningPnL += safePnl(trade);
      return { index: i, date: trade.date, pnl: safePnl(trade), cumulative: runningPnL };
    });
    
    // Calculate max drawdown
    let peak = 0, maxDrawdown = 0, maxDrawdownPercent = 0;
    equityCurve.forEach(point => {
      if (point.cumulative > peak) peak = point.cumulative;
      const drawdown = peak - point.cumulative;
      if (drawdown > maxDrawdown) {
        maxDrawdown = drawdown;
        maxDrawdownPercent = peak > 0 ? (drawdown / peak * 100) : 0;
      }
    });


    // === REVENGE TRADING DETECTION ===
    const revengeTradingInstances = [];
    for (let i = 1; i < sortedByDate.length; i++) {
      const prevTrade = sortedByDate[i - 1];
      const currTrade = sortedByDate[i];
      if (prevTrade.date === currTrade.date && safePnl(prevTrade) < 0) {
        let isQuickFollow = false;
        if (prevTrade.exitTime && currTrade.entryTime) {
          const getMinutes = (timeStr) => {
            if (!timeStr) return null;
            if (timeStr.includes('/')) {
              const parts = timeStr.split(' ');
              if (parts.length >= 3) {
                const [h, m] = parts[1].split(':');
                let hour = parseInt(h);
                if (parts[2]?.toUpperCase() === 'PM' && hour !== 12) hour += 12;
                if (parts[2]?.toUpperCase() === 'AM' && hour === 12) hour = 0;
                return hour * 60 + parseInt(m);
              }
            } else {
              const [h, m] = timeStr.split(':');
              return parseInt(h) * 60 + parseInt(m);
            }
            return null;
          };
          const prevExit = getMinutes(prevTrade.exitTime);
          const currEntry = getMinutes(currTrade.entryTime);
          if (prevExit !== null && currEntry !== null) {
            isQuickFollow = (currEntry - prevExit) <= 10;
          }
        }
        if (isQuickFollow || safePnl(currTrade) < 0) {
          const existingCluster = revengeTradingInstances.find(r => 
            r.trades.some(t => t === prevTrade || t === currTrade)
          );
          if (existingCluster) {
            if (!existingCluster.trades.includes(currTrade)) {
              existingCluster.trades.push(currTrade);
              existingCluster.totalPnL += safePnl(currTrade);
            }
          } else if (isQuickFollow) {
            revengeTradingInstances.push({
              date: currTrade.date,
              trades: [prevTrade, currTrade],
              totalPnL: safePnl(prevTrade) + safePnl(currTrade),
              trigger: prevTrade
            });
          }
        }
      }
    }
    const revengeTradingStats = {
      instances: revengeTradingInstances.length,
      totalPnL: revengeTradingInstances.reduce((sum, r) => sum + r.totalPnL, 0),
      avgPnL: revengeTradingInstances.length > 0 
        ? revengeTradingInstances.reduce((sum, r) => sum + r.totalPnL, 0) / revengeTradingInstances.length : 0,
      worstInstance: revengeTradingInstances.length > 0 
        ? revengeTradingInstances.reduce((worst, r) => r.totalPnL < worst.totalPnL ? r : worst) : null
    };


    // === THIS WEEK VS LAST WEEK ===
    // Sunday-anchored week boundary, computed via timeService primitives so
    // the comparison is string-based and immune to UTC drift. weekStartOfDateStr
    // returns ISO Monday; shift -1 day for Sun-as-week-start.
    const todayStr = getCurrentTradingDay();
    const thisWeekStart = addDaysToDateStr(weekStartOfDateStr(todayStr), -1);
    const lastWeekStart = addDaysToDateStr(thisWeekStart, -7);
    const lastWeekEnd   = addDaysToDateStr(thisWeekStart, -1);

    const thisWeekTrades = filteredTrades.filter(t => t.date >= thisWeekStart);
    const lastWeekTrades = filteredTrades.filter(t => tradeDateInRange(t.date, lastWeekStart, lastWeekEnd));
    
    const thisWeekStats = calcTradeStats(thisWeekTrades);
    const thisWeekR = calcRStats(thisWeekTrades);
    const lastWeekStats = calcTradeStats(lastWeekTrades);
    const lastWeekR = calcRStats(lastWeekTrades);
    const weekComparison = {
      thisWeek: {
        trades: thisWeekStats.totalTrades,
        pnl: thisWeekStats.totalPnL,
        winRate: thisWeekStats.totalTrades > 0 ? thisWeekStats.winRate.toFixed(1) : 0,
        avgR: thisWeekR.avgR.toFixed(2)
      },
      lastWeek: {
        trades: lastWeekStats.totalTrades,
        pnl: lastWeekStats.totalPnL,
        winRate: lastWeekStats.totalTrades > 0 ? lastWeekStats.winRate.toFixed(1) : 0,
        avgR: lastWeekR.avgR.toFixed(2)
      }
    };


    // === SETUP + DIRECTION MATRIX ===
    const setupDirectionMatrix = {};
    filteredTrades.forEach(trade => {
      const setup = trade.setup || 'Unknown';
      const direction = trade.direction || 'unknown';
      const key = `${setup}|${direction}`;
      if (!setupDirectionMatrix[key]) {
        setupDirectionMatrix[key] = { setup, direction, count: 0, wins: 0, totalPnL: 0 };
      }
      setupDirectionMatrix[key].count++;
      if (safePnl(trade) > 0) setupDirectionMatrix[key].wins++;
      setupDirectionMatrix[key].totalPnL += safePnl(trade);
    });
    Object.values(setupDirectionMatrix).forEach(data => {
      data.winRate = (data.wins / data.count * 100).toFixed(1);
      data.avgPnL = (data.totalPnL / data.count).toFixed(2);
    });
    
    // === SETUP + TIME (SESSION) MATRIX ===
    const setupTimeMatrix = {};
    tradesWithTime.forEach(trade => {
      const setup = trade.setup || 'Unknown';
      const hour = getHour(trade);
      if (hour === null) return;
      let session = hour < 11 ? 'Morning' : hour < 14 ? 'Midday' : 'Afternoon';
      const key = `${setup}|${session}`;
      if (!setupTimeMatrix[key]) {
        setupTimeMatrix[key] = { setup, session, count: 0, wins: 0, totalPnL: 0 };
      }
      setupTimeMatrix[key].count++;
      if (safePnl(trade) > 0) setupTimeMatrix[key].wins++;
      setupTimeMatrix[key].totalPnL += safePnl(trade);
    });
    Object.values(setupTimeMatrix).forEach(data => {
      data.winRate = (data.wins / data.count * 100).toFixed(1);
      data.avgPnL = (data.totalPnL / data.count).toFixed(2);
    });
    
    // === SETUP + SYMBOL MATRIX ===
    const setupSymbolMatrix = {};
    filteredTrades.forEach(trade => {
      const setup = trade.setup || 'Unknown';
      const symbol = trade.symbol || 'Unknown';
      const key = `${setup}|${symbol}`;
      if (!setupSymbolMatrix[key]) {
        setupSymbolMatrix[key] = { setup, symbol, count: 0, wins: 0, totalPnL: 0 };
      }
      setupSymbolMatrix[key].count++;
      if (safePnl(trade) > 0) setupSymbolMatrix[key].wins++;
      setupSymbolMatrix[key].totalPnL += safePnl(trade);
    });
    Object.values(setupSymbolMatrix).forEach(data => {
      data.winRate = (data.wins / data.count * 100).toFixed(1);
      data.avgPnL = (data.totalPnL / data.count).toFixed(2);
    });


    // === POSITION SIZING ANALYSIS ===
    const positionSizePerformance = {};
    filteredTrades.forEach(trade => {
      const qty = parseInt(trade.quantity) || 1;
      let bucket = qty <= 3 ? qty.toString() : qty <= 5 ? '4-5' : '6+';
      if (!positionSizePerformance[bucket]) {
        positionSizePerformance[bucket] = { bucket, count: 0, wins: 0, totalPnL: 0, rMultiples: [], quantities: [] };
      }
      positionSizePerformance[bucket].count++;
      if (safePnl(trade) > 0) positionSizePerformance[bucket].wins++;
      positionSizePerformance[bucket].totalPnL += safePnl(trade);
      if (trade.rMultiple) positionSizePerformance[bucket].rMultiples.push(parseFloat(trade.rMultiple));
      positionSizePerformance[bucket].quantities.push(qty);
    });
    Object.values(positionSizePerformance).forEach(data => {
      data.winRate = (data.wins / data.count * 100).toFixed(1);
      data.avgPnL = (data.totalPnL / data.count).toFixed(2);
      data.avgR = data.rMultiples.length > 0 
        ? (data.rMultiples.reduce((a, b) => a + b, 0) / data.rMultiples.length).toFixed(2) : '0';
      data.avgQty = (data.quantities.reduce((a, b) => a + b, 0) / data.quantities.length).toFixed(1);
    });
    const sortedBySizePerformance = Object.values(positionSizePerformance)
      .filter(d => d.count >= 3)
      .sort((a, b) => parseFloat(b.winRate) - parseFloat(a.winRate));
    const optimalSize = sortedBySizePerformance.length > 0 ? sortedBySizePerformance[0] : null;


    // === CONFLUENCE ANALYSIS ===
    const newBucket = () => ({ count: 0, wins: 0, totalPnL: 0, rMultiples: [] });
    const confluencePerformance = {};
    filteredTrades.forEach(trade => {
      if (trade.confluences && Array.isArray(trade.confluences)) {
        const dir = trade.direction === 'short' ? 'short' : 'long';
        const rVal = trade.rMultiple ? parseFloat(trade.rMultiple) : null;
        const hour = trade.entryTime?.includes(':') ? parseInt(trade.entryTime.split(':')[0], 10) : null;
        const maeVal = parseFloat(trade.mae);
        const mfeVal = parseFloat(trade.mfe);

        trade.confluences.forEach(confId => {
          if (!confluencePerformance[confId]) {
            confluencePerformance[confId] = {
              id: confId, count: 0, wins: 0, totalPnL: 0, rMultiples: [],
              long: newBucket(), short: newBucket(),
              hourly: {}, atms: {},
              maeValues: [], mfeValues: []
            };
          }
          const cp = confluencePerformance[confId];
          cp.count++;
          if (safePnl(trade) > 0) cp.wins++;
          cp.totalPnL += safePnl(trade);
          if (rVal && isFinite(rVal)) cp.rMultiples.push(rVal);

          // Direction split
          const db = cp[dir];
          db.count++;
          if (safePnl(trade) > 0) db.wins++;
          db.totalPnL += safePnl(trade);
          if (rVal && isFinite(rVal)) db.rMultiples.push(rVal);

          // Hourly bucket
          if (hour !== null && !isNaN(hour)) {
            if (!cp.hourly[hour]) cp.hourly[hour] = { hour, ...newBucket() };
            const hb = cp.hourly[hour];
            hb.count++;
            if (safePnl(trade) > 0) hb.wins++;
            hb.totalPnL += safePnl(trade);
            if (rVal && isFinite(rVal)) hb.rMultiples.push(rVal);
          }

          // ATM pairing
          if (trade.atmStrategy) {
            if (!cp.atms[trade.atmStrategy]) cp.atms[trade.atmStrategy] = { atm: trade.atmStrategy, ...newBucket() };
            const ab = cp.atms[trade.atmStrategy];
            ab.count++;
            if (safePnl(trade) > 0) ab.wins++;
            ab.totalPnL += safePnl(trade);
            if (rVal && isFinite(rVal)) ab.rMultiples.push(rVal);
          }

          // MAE/MFE
          if (isFinite(maeVal)) cp.maeValues.push(maeVal);
          if (isFinite(mfeVal)) cp.mfeValues.push(mfeVal);
        });
      }
    });

    const deriveBucket = (b) => {
      b.winRate = b.count > 0 ? (b.wins / b.count * 100).toFixed(1) : '0';
      b.avgPnL = b.count > 0 ? (b.totalPnL / b.count).toFixed(2) : '0';
      b.avgR = b.rMultiples.length > 0
        ? (b.rMultiples.reduce((a, c) => a + c, 0) / b.rMultiples.length).toFixed(2) : '0';
    };
    Object.values(confluencePerformance).forEach(data => {
      deriveBucket(data);
      deriveBucket(data.long);
      deriveBucket(data.short);

      // Hourly array
      data.hourlyArray = Object.values(data.hourly).map(h => { deriveBucket(h); return h; })
        .sort((a, b) => a.hour - b.hour);
      delete data.hourly;

      // ATM array (all ATMs used with this confluence)
      data.atmArray = Object.values(data.atms).map(a => { deriveBucket(a); return a; })
        .sort((a, b) => b.count - a.count);
      delete data.atms;

      // MAE/MFE averages
      data.avgMAE = data.maeValues.length > 0
        ? data.maeValues.reduce((a, b) => a + b, 0) / data.maeValues.length : null;
      data.avgMFE = data.mfeValues.length > 0
        ? data.mfeValues.reduce((a, b) => a + b, 0) / data.mfeValues.length : null;
      data.maeCount = data.maeValues.length;
      data.mfeCount = data.mfeValues.length;
      data.mfeMaeRatio = (data.avgMAE && data.avgMAE !== 0 && data.avgMFE)
        ? (data.avgMFE / Math.abs(data.avgMAE)).toFixed(2) : null;
      delete data.maeValues;
      delete data.mfeValues;
    });
    
    // Analyze confluence combinations (pairs)
    const confluenceCombinations = {};
    filteredTrades.forEach(trade => {
      if (trade.confluences && Array.isArray(trade.confluences) && trade.confluences.length >= 2) {
        // Create pairs of confluences
        for (let i = 0; i < trade.confluences.length; i++) {
          for (let j = i + 1; j < trade.confluences.length; j++) {
            const pair = [trade.confluences[i], trade.confluences[j]].sort().join('+');
            if (!confluenceCombinations[pair]) {
              confluenceCombinations[pair] = { 
                pair, 
                ids: [trade.confluences[i], trade.confluences[j]].sort(),
                count: 0, 
                wins: 0, 
                totalPnL: 0 
              };
            }
            confluenceCombinations[pair].count++;
            if (safePnl(trade) > 0) confluenceCombinations[pair].wins++;
            confluenceCombinations[pair].totalPnL += safePnl(trade);
          }
        }
      }
    });
    Object.values(confluenceCombinations).forEach(data => {
      data.winRate = data.count > 0 ? (data.wins / data.count * 100).toFixed(1) : '0';
      data.avgPnL = data.count > 0 ? (data.totalPnL / data.count).toFixed(2) : '0';
    });
    
    // Confluence count analysis (how many confluences = better results?)
    const confluenceCountPerformance = {};
    filteredTrades.forEach(trade => {
      const count = trade.confluences?.length || 0;
      const bucket = count === 0 ? '0' : count <= 2 ? '1-2' : count <= 4 ? '3-4' : '5+';
      if (!confluenceCountPerformance[bucket]) {
        confluenceCountPerformance[bucket] = { bucket, count: 0, wins: 0, totalPnL: 0, rMultiples: [] };
      }
      confluenceCountPerformance[bucket].count++;
      if (safePnl(trade) > 0) confluenceCountPerformance[bucket].wins++;
      confluenceCountPerformance[bucket].totalPnL += safePnl(trade);
      if (trade.rMultiple) {
        const rv = parseFloat(trade.rMultiple);
        if (isFinite(rv)) confluenceCountPerformance[bucket].rMultiples.push(rv);
      }
    });
    Object.values(confluenceCountPerformance).forEach(data => {
      deriveBucket(data);
    });
    
    const sortedConfluences = Object.values(confluencePerformance)
      .filter(d => d.count >= 3)
      .sort((a, b) => parseFloat(b.winRate) - parseFloat(a.winRate));
    const sortedCombinations = Object.values(confluenceCombinations)
      .filter(d => d.count >= 3)
      .sort((a, b) => parseFloat(b.winRate) - parseFloat(a.winRate));


    // === CALCULATE OVERALL GRADE ===
    let gradeScore = 50;
    if (winRate >= 60) gradeScore += 15;
    else if (winRate >= 50) gradeScore += 10;
    else if (winRate >= 40) gradeScore += 5;
    else gradeScore -= 10;
    
    if (profitFactor >= 2) gradeScore += 15;
    else if (profitFactor >= 1.5) gradeScore += 10;
    else if (profitFactor >= 1) gradeScore += 5;
    else gradeScore -= 10;
    
    if (avgR >= 1.5) gradeScore += 15;
    else if (avgR >= 1) gradeScore += 10;
    else if (avgR >= 0.5) gradeScore += 5;
    else if (avgR < 0) gradeScore -= 10;
    
    const mistakeRatio = filteredTrades.length > 0 ? tradesWithMistakes / filteredTrades.length : 0;
    if (mistakeRatio === 0) gradeScore += 10;
    else if (mistakeRatio <= 0.1) gradeScore += 5;
    else if (mistakeRatio >= 0.3) gradeScore -= 10;
    
    if (greenDays > redDays * 1.5) gradeScore += 5;
    else if (redDays > greenDays * 1.5) gradeScore -= 5;
    
    let grade, gradeColor;
    if (gradeScore >= 90) { grade = 'A+'; gradeColor = 'text-green-400'; }
    else if (gradeScore >= 85) { grade = 'A'; gradeColor = 'text-green-500'; }
    else if (gradeScore >= 80) { grade = 'A-'; gradeColor = 'text-green-500'; }
    else if (gradeScore >= 75) { grade = 'B+'; gradeColor = 'text-blue-400'; }
    else if (gradeScore >= 70) { grade = 'B'; gradeColor = 'text-blue-500'; }
    else if (gradeScore >= 65) { grade = 'B-'; gradeColor = 'text-blue-500'; }
    else if (gradeScore >= 60) { grade = 'C+'; gradeColor = 'text-yellow-400'; }
    else if (gradeScore >= 55) { grade = 'C'; gradeColor = 'text-yellow-500'; }
    else if (gradeScore >= 50) { grade = 'C-'; gradeColor = 'text-yellow-500'; }
    else if (gradeScore >= 45) { grade = 'D+'; gradeColor = 'text-orange-400'; }
    else if (gradeScore >= 40) { grade = 'D'; gradeColor = 'text-orange-500'; }
    else { grade = 'F'; gradeColor = 'text-red-500'; }


    return {
      // Core metrics
      totalTrades: filteredTrades.length,
      totalPnL,
      winRate: winRate.toFixed(1),
      profitFactor: profitFactor === Infinity ? '∞' : profitFactor.toFixed(2),
      avgWin,
      avgLoss,
      avgR: avgR.toFixed(2),
      totalR: totalR.toFixed(2),
      grade: { letter: grade, color: gradeColor, score: gradeScore },
      
      // Setup analysis
      setupPerformance,
      bestSetup,
      mostProfitableSetup,
      
      // Symbol analysis
      symbolPerformance: sortedSymbols,
      
      // Direction analysis
      directionAnalysis,
      
      // ATM analysis
      atmPerformance: sortedAtm,
      
      // Time analysis
      timeAnalysis: {
        hourlyPerformance,
        bestHour: bestHour ? { hour: parseInt(bestHour[0]), ...bestHour[1] } : null,
        worstHour: worstHour ? { hour: parseInt(worstHour[0]), ...worstHour[1] } : null,
        dayOfWeek: activeDays,
        sessions: sessionPerformance,
        tradesWithTime: tradesWithTime.length
      },
      
      // Streaks
      streaks: streaksData,
      
      // Daily stats
      dailyStats: {
        tradingDays: tradingDays.length,
        greenDays,
        redDays,
        avgDailyPnL,
        avgTradesPerDay: avgTradesPerDay.toFixed(1),
        bestDay: bestDay ? { date: bestDay[0], ...bestDay[1] } : null,
        worstDay: worstDay ? { date: worstDay[0], ...worstDay[1] } : null
      },
      
      // Recent vs historical
      recentPerformance,
      olderPerformance,
      
      // Account breakdown
      accountPerformance,
      
      // Mistakes
      topMistakes,
      mistakeStats: {
        tradesWithMistakes,
        mistakePnL,
        mistakeRate: (tradesWithMistakes / filteredTrades.length * 100).toFixed(1)
      },
      
      // Equity Curve
      equityCurve,
      maxDrawdown,
      maxDrawdownPercent: maxDrawdownPercent.toFixed(1),
      
      // Revenge Trading
      revengeTrading: revengeTradingStats,
      
      // Week Comparison
      weekComparison,
      
      // Setup Matrices
      setupDirectionMatrix: Object.values(setupDirectionMatrix).sort((a, b) => b.totalPnL - a.totalPnL),
      setupTimeMatrix: Object.values(setupTimeMatrix).sort((a, b) => b.totalPnL - a.totalPnL),
      setupSymbolMatrix: Object.values(setupSymbolMatrix).sort((a, b) => b.totalPnL - a.totalPnL),
      
      // Position Sizing
      positionSizing: {
        performance: Object.values(positionSizePerformance).sort((a, b) => parseInt(a.bucket) - parseInt(b.bucket)),
        optimal: optimalSize
      },
      
      // Confluence Analysis
      confluenceAnalysis: {
        individual: sortedConfluences,
        combinations: sortedCombinations.slice(0, 10), // Top 10 combinations
        byCount: Object.values(confluenceCountPerformance).sort((a, b) => {
          const order = { '0': 0, '1-2': 1, '3-4': 2, '5+': 3 };
          return order[a.bucket] - order[b.bucket];
        }),
        allConfluences: Object.values(confluencePerformance).sort((a, b) => b.count - a.count)
      },
      
      // Suggestions
      suggestions: generateSuggestions(setupPerformance, topMistakes, directionAnalysis, 
        tradesWithTime.length > 0 ? sessionPerformance : null, recentPerformance, olderPerformance, 
        streaksData, avgR, revengeTradingStats, weekComparison, optimalSize)
    };
  }, [trades, accounts, getFilteredTrades, generateSuggestions]);



  // ============================================
  // GENERATE DAILY REVIEW — Coach-Engine-powered
  // ============================================
  // This function used to hand-roll ~500 lines of metrics, grade scoring, and
  // feedback. All of that now lives in the Coach Engine under
  // `services/coach/scopes/day.js` (analyzers in `analyzers/day/*`). We keep
  // the legacy return shape for zero-touch compatibility with the Daily Review
  // modal via `toLegacyReviewShape`.
  const generateDailyReview = useCallback((reviewDate) => {
    const dateToReview = reviewDate || getCurrentTradingDay();

    const dayTrades = trades.filter(trade => {
      if (trade.date !== dateToReview) return false;
      if (activeAccountsOnly && trade.accountId && !activeAccountIds.includes(trade.accountId)) {
        return false;
      }
      return true;
    });

    const journalEntry = journalEntries[dateToReview] || {};
    const getAccountById = (id) => accounts.find(acc => acc.id === id);

    const review = coach.reviewDay(dateToReview, {
      trades: dayTrades,
      allTrades: getFilteredTrades(trades),
      journalEntry,
      allJournalEntries: journalEntries,
      accounts,
      getTradeNetPnL,
      getAccountById,
      tier: 'free'
    });

    return toLegacyReviewShape({
      day: review.day,
      observations: review.observations,
      grade: review.grade,
      allJournalEntries: journalEntries,
      allTrades: getFilteredTrades(trades),
      getTradeNetPnL,
      getAccountById
    });
  }, [trades, accounts, journalEntries, activeAccountsOnly, activeAccountIds, getFilteredTrades]);

  // ============================================
  // RETURN HOOK VALUES
  // ============================================
  return {
    analyzePatterns,
    generateSuggestions,
    parseNaturalLanguageTrade,
    generateDailyReview
  };
};

export default useAIAnalysis;
